// KytV2 — KYT's rebuilt screen (UI rebuild batch 4), drawn only when lib/ui-v2.ts switches batch 4 on: App.tsx routes /kyt
// here, else to today's Kyt.tsx. The UI-rebuild lane designed the payments view and handed it over on 27 Oct 2026 (spec:
// docs/ui-rebuild/handoff/KYT.md); the KYT lane adopted it the same day and redrew Wallets and System on the kit after it
// (the Command Centre, 27 Oct 2026: today's lists kept as they are — the one-list coverage view is N-036 (4), held). This
// file and components/v2/kyt/* are the KYT lane's, beside Kyt.tsx. The words and the rules are Kyt.tsx's own, IMPORTED —
// never a copy (the hand-off's rules.ts is gone).
//
// ASTRA'S KYT, under the operator's rulings: the screen leads with the incoming payments that need him — information first:
// who paid, how much, into which wallet, what the screening found (attribution, sources, list status) — and the lane's
// level word only as a quiet tag after it. No verdict column, no "Checked", no green, no new block or refusal. A row opens
// its SCREENING ASSESSMENT (?payment=<id>), which says what is unresolved before any source detail; the lane's own full
// trace is one tap further. "Not fully checked" is said ONCE, over the list. The answer before an exchange deposit is one
// tap off the head (?deposit=1). Provider status, the watched wallets and the holdings are off the default view: the
// Wallets and System tabs (components/v2/kyt/Wallets.tsx, System.tsx). Astra's one-list "Wallet coverage" is HELD
// (N-036 (4)): drawn at ?view=coverage only, marked held, linked from nowhere.
//
// Kept from Kyt.tsx, by import: the same reads and query keys (['kyt','overview'], ['kyt','incoming',hours], both every
// 60 s, the window's last answer kept while the next is read; ['treasury','balances'] while Wallets is open); Refresh's
// invalidation (never the paid transfers read); the window (24 h · 3 days · 7 days); the alert count and its stop words —
// on every view, an alert on incoming funds comes before everything else; alerts counted but not listed; the "not read
// in full" rule (no calm claim over a wallet not being read); "No KYT group"; "nothing watched"; sorting (newest first by
// default — the operator, 8 Oct); the payment trace and the wallet trace themselves.
import { useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { api, type KytIncoming, type KytPayment } from '../../../lib/api';
import { firstRead, readState, type ReadState } from '../../../lib/read-state';
import { dubaiDate, fmtAmount, fmtRelative } from '../../../lib/format';
import { networkWord,
  PaymentTrace, calmDay, count, countedPayments, dayTotals, dollars, emptyPaymentsWords, firstSentence, glSenderNames, mainRisk,
  notWorking, paymentAlerts, paymentsUnread, providerStrip, readFailure, riskRank, seatOf, share, sortPayments, unlistedAlerts, whole,
  windowLabel, windowWords, type SenderName,
} from '../../Kyt';
import { PeriodChips, periodDays, periodWords, type PeriodKey, type PeriodValue } from '../../../components/PeriodChips';
import {
  EmptyState, FilterChip, Notice, PageHead, Panel, Register, SearchBar, SkeletonRows, StateLine, SummaryStrip, Tabs, useFoldParam, useSortParam,
  type RegisterColumn, type RegisterGroup, type RegisterSort,
} from '../../../components/v2/kit';
import { shortAddress } from '../../../components/kit';
import { Assessment } from '../../../components/v2/kyt/Assessment';
import { CoverageView } from '../../../components/v2/kyt/Coverage';
import { DepositSheet } from '../../../components/v2/kyt/DepositSheet';
import { CardLine, Exposed, Finding, GlScore, LevelTag, Share, Time, Who, byDubaiDay, compactWallet, stripItems, useTier, type Tier } from '../../../components/v2/kyt/parts';
import { SystemV2 } from '../../../components/v2/kyt/System';
import { WalletsV2 } from '../../../components/v2/kyt/Wallets';

/** The payments that need him, in the lane's own seats: an alert, a watch, or one not fully checked (no result yet, a
 *  check that could not cover everything, one being checked) — and a look-alike sender, whatever its size (address
 *  poisoning, 3 Nov 2026). A clean and a small payment are on the All list. */
const needsAttention = (p: KytPayment): boolean => p.kind === 'alert' || p.kind === 'watch' || p.kind === 'unchecked' || !!p.poisoning;

/** The views: the payments (the default), the treasury's wallets, the screener itself — and the held coverage option. */
type View = 'payments' | 'wallets' | 'system' | 'coverage';

export function KytV2() {
  const [search] = useSearchParams();
  const v = search.get('view');
  return <Screen view={v === 'wallets' ? 'wallets' : v === 'system' ? 'system' : v === 'coverage' ? 'coverage' : 'payments'} />;
}

// ── The filters: which seats the list shows, and its order ──
type Show = 'attention' | 'all' | 'alert' | 'watch' | 'unchecked' | 'clean' | 'small';
const SHOWS: Array<{ key: Show; label: string; has: (p: KytPayment) => boolean }> = [
  { key: 'attention', label: 'Needs attention', has: needsAttention },
  { key: 'all', label: 'All', has: () => true },
  { key: 'alert', label: 'Alert', has: (p) => p.kind === 'alert' },
  { key: 'watch', label: 'Watch', has: (p) => p.kind === 'watch' },
  { key: 'unchecked', label: 'Not fully checked', has: (p) => p.kind === 'unchecked' },
  { key: 'clean', label: 'Clean', has: (p) => p.kind === 'clean' },
  { key: 'small', label: 'Small', has: (p) => p.kind === 'small' },
];
/** The sort is the kit's (`?sort=key:dir`, a column's key); these chips are the phone's way to it, where there are no
 *  heads to press. An old link's bare `newest` / `risk` still reads. */
const NEWEST: RegisterSort = { key: 'time', dir: 'desc' };
const SORTS: ReadonlyArray<{ sort: RegisterSort; label: string }> = [
  { sort: NEWEST, label: 'Newest first' },
  { sort: { key: 'level', dir: 'desc' }, label: 'Highest risk first' },
  { sort: { key: 'amount', dir: 'desc' }, label: 'Largest first' },
];
const sameSort = (a: RegisterSort | null, b: RegisterSort): boolean => !!a && a.key === b.key && a.dir === b.dir;
const legacySort = (s: RegisterSort | null): RegisterSort | null => (s?.key === 'newest' ? NEWEST : s?.key === 'risk' ? { key: 'level', dir: 'desc' } : s);
/** The foot's words for the order in force. */
const sortWords = (s: RegisterSort | null): string => {
  const known = SORTS.find((x) => sameSort(s, x.sort));
  if (known) return known.label;
  if (!s) return 'Newest first';
  const head: Record<string, string> = { time: 'time', wallet: 'wallet', party: 'party', from: 'sender', amount: 'amount', finding: 'finding', share: 'share', exposure: 'exposure', score: 'GL score', level: 'risk' };
  return `By ${head[s.key] ?? s.key}, ${s.dir === 'asc' ? 'ascending' : 'descending'}`;
};
const ROWS_SHOWN = 25;

const PERIOD_KEYS: ReadonlyArray<PeriodKey> = ['today', 'yesterday', 'week', 'month', 'quarter', 'year', 'custom'];
/** The period in the URL (`?period=`, with `from`/`to` for dates): THIS WEEK by default — a monitoring list is never empty
 *  at 00:30 the way "today" is. Yesterday is the kit's own chip (3bcfd55f). The two days go to the read as the worker
 *  takes them (28 Oct 2026). */
export function periodOf(search: URLSearchParams): PeriodValue {
  const k = search.get('period');
  if (k === 'custom') return { key: 'custom', from: search.get('from') ?? undefined, to: search.get('to') ?? undefined };
  return { key: k && PERIOD_KEYS.includes(k as PeriodKey) && k !== 'custom' ? (k as PeriodKey) : 'week' };
}
/** The read's query key for the URL's period — one function for the screen and its tests. */
export function incomingKeyOf(search: URLSearchParams): ReadonlyArray<string | null> {
  const d = periodDays(periodOf(search));
  return ['kyt', 'incoming', d.from ?? null, d.to ?? null];
}
/** The wallet filter in the URL: `?into=TRC20:T…` one desk wallet, `?into=TRC20:*` a chain, nothing = all. */
const pickOf = (into: string | null) => (w: { chain: string; address: string }): boolean =>
  !into || (into.endsWith(':*') ? w.chain.toUpperCase() === into.slice(0, -2).toUpperCase() : `${w.chain}:${w.address}`.toLowerCase() === into.toLowerCase());
const intoWords = (inc: KytIncoming, into: string): string => {
  if (into.endsWith(':*')) return `All ${networkWord(into.slice(0, -2))}`;
  const w = inc.wallets.find((x) => `${x.chain}:${x.address}`.toLowerCase() === into.toLowerCase());
  return w?.label ?? shortAddress(into.slice(into.indexOf(':') + 1));
};

/** One URL change: `push` for opening a sheet (Back closes it), replace for a filter. */
type Url = { set: (patch: Record<string, string | null>, push?: boolean) => void; openSheet: (patch: Record<string, string>) => void; closeSheet: (keys: string[]) => void };

function Screen({ view }: { view: View }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const openedHere = useRef(false);

  const period = periodOf(search);
  const days = periodDays(period);
  const into = search.get('into');
  const pick = pickOf(into);

  const ov = useQuery({ queryKey: ['kyt', 'overview'], queryFn: () => api.kytOverview(), refetchInterval: 60_000 });
  const incoming = useQuery({ queryKey: [...incomingKeyOf(search)], queryFn: () => api.kytIncoming(days.from ? { from: days.from, to: days.to } : { hours: 24 }), refetchInterval: 60_000, placeholderData: (prev) => prev });
  // What the treasury holds — the Books' own figures, the Treasury tab's read (its cache is shared): read while Wallets is open.
  const treasury = useQuery({ queryKey: ['treasury', 'balances'], queryFn: () => api.treasuryBalances(), enabled: view === 'wallets' });
  // THE FIRST READ opened offline WAITS (design system Appendix B; lib/read-state.ts firstRead): a read the client paused, or one
  // that failed only because this device has no connection, is not a failure and not "Not read" — one grey line says the device
  // is offline, and the screener is read as soon as it is back. Measured 27 Oct 2026: a page opened offline read `online: true`
  // from the read client and `navigator.onLine === false` from the browser, so its first read ran, failed, and was drawn in red.
  const ovFirst = firstRead(ov);
  const incFirst = firstRead(incoming);
  const offline = ovFirst === 'offline' || incFirst === 'offline';
  const incRead: ReadState = incFirst === 'offline' ? 'waiting' : readState(incoming);
  const ovRead: ReadState = ovFirst === 'offline' ? 'waiting' : readState(ov);
  const inc = incoming.data;
  const d = ov.data;
  // The answer is of the window asked for — else it is the last window's, kept while the next is read (placeholderData).
  // An answer that names no range is a worker answering by hours: taken as it is, never "Reading…" for ever.
  const fresh = !!inc && (!inc.range || (days.from ? inc.range.from === days.from && (!days.to || inc.range.to === days.to) : true));
  const depositWords = days.from ? periodWords(period) : windowWords(inc?.hours ?? 24);

  const url: Url = {
    set: (patch, push = false) => {
      const next = new URLSearchParams(search);
      for (const [k, v] of Object.entries(patch)) { if (v === null) next.delete(k); else next.set(k, v); }
      setSearch(next, { replace: !push });
    },
    openSheet: (patch) => { openedHere.current = true; url.set(patch, true); },
    closeSheet: (keys) => {
      if (openedHere.current) { openedHere.current = false; navigate(-1); return; }
      url.set(Object.fromEntries(keys.map((k) => [k, null])));
    },
  };
  const refresh = () => qc.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'kyt' && query.queryKey[1] !== 'transfers' });

  // ── What stands above every view: the failures and the holds first ──
  const bothDown = !offline && ov.isError && incoming.isError && !d && !inc;
  const incFail = incRead === 'failed' ? readFailure(incoming.error) : null;
  const ovFail = ovRead === 'failed' ? readFailure(ov.error) : null;
  // The worker's alarm (the bot down: Anthropic credits used up, or the key refused), its Telegram dot dropped: the screen adds no
  // words of its own in front of it (1 Nov 2026: "Screening brain is down — <its sentence>" read as two facts in one line).
  const alarm = d?.meta.alarm ? firstSentence(d.meta.alarm.replace(/^[^\p{L}\p{N}]+/u, '')) : null;
  // Counts and claims only from a read that succeeded (Appendix B).
  const known = incRead === 'known' && !!inc;
  const alerts = known ? paymentAlerts(inc) : null;
  // The strip of four figures (Alerts · Watch · Not checked · Wallets not read) stands over Payments and Wallets for the
  // window: it says the alerts, so the red line does not say them again there (28 Oct 2026).
  const strip = known && !!inc && inc.wallets.length > 0 && (view === 'payments' || view === 'wallets');

  // The tabs: a view not on screen says its trouble on its tab — the alerts, the wallets not being read; and what is not
  // working on System, counted as today's chip counts it (a provider not answering, a list not refreshed, a failed night
  // or sender check — Kyt.tsx's strip, aged against the BFF's own read time, never the viewer's clock).
  const readAt = d ? Date.parse(d.fetched_at) : NaN;
  const nowMs = Number.isFinite(readAt) ? readAt : Date.now();
  const broken = d ? notWorking(providerStrip(d, nowMs)) : 0;
  const unreadWallets = known ? inc.wallets.filter(paymentsUnread).length : 0;
  const tabs = [
    { id: 'payments', label: alerts && view !== 'payments' ? `Incoming payments · ${count(alerts.n, 'alert', 'alerts')}` : 'Incoming payments', to: '/kyt' },
    { id: 'wallets', label: unreadWallets > 0 ? `Wallets · ${unreadWallets} not read` : 'Wallets', to: '/kyt?view=wallets' },
    { id: 'system', label: broken > 0 ? `System · ${broken} not working` : 'System', to: '/kyt?view=system' },
  ];
  const budgetOut = d ? d.spend.arkhamToday >= d.spend.arkhamBudget : false;
  const sub = view === 'payments'
    ? incRead === 'failed' ? 'Payments not read' : inc ? `Read ${fmtRelative(inc.fetched_at)}` : incFirst === 'offline' ? 'Offline' : 'Reading…'
    : d ? `Read ${fmtRelative(d.fetched_at)}` : ovRead === 'failed' ? 'Screener not read' : ovFirst === 'offline' ? 'Offline' : 'Reading…';
  const retryBoth = <button type="button" className="btn-link text-body" onClick={() => { void ov.refetch(); void incoming.refetch(); }}>Try again</button>;

  return (
    <>
      <PageHead
        title="KYT"
        sub={sub}
        actions={
          <>
            {view === 'payments' && incRead !== 'failed' && (
              <button type="button" className="btn-secondary px-3 text-body" onClick={() => url.openSheet({ deposit: '1' })}>Before a deposit</button>
            )}
            <button type="button" className="btn-secondary px-3 text-body disabled:opacity-50" disabled={incoming.isFetching || ov.isFetching} onClick={() => void refresh()}>
              {incoming.isFetching || ov.isFetching ? 'Reading…' : 'Refresh'}
            </button>
          </>
        }
      />
      <Tabs label="KYT views" items={tabs} current={view === 'coverage' ? 'wallets' : view} wrap />

      {/* Offline, nothing read yet: one grey line, no Try again — the screener is read as soon as the device is back online. */}
      {offline && <Notice tone="neutral">Offline — waiting to read the screener.</Notice>}
      {/* Reads that failed: one line, red — never drawn like an empty list. Both down is one line. */}
      {bothDown && incFail && <Notice tone="bad" action={retryBoth}>Nothing is read — {incFail.why}.</Notice>}
      {/* The one banner that matters, on every view: the screening brain is down — in the worker's words, the rest of them under. */}
      {!bothDown && alarm && <Notice tone="bad">{alarm.first}.{alarm.rest && <span className="block text-meta">{alarm.rest}</span>}</Notice>}
      {/* An alert on incoming funds comes before everything else on this screen — whichever view is open. */}
      {!bothDown && alerts && !strip && (
        <Notice tone="bad" action={view !== 'payments' ? <Link to="/kyt" className="tap-44 btn-link text-body">See payments</Link> : undefined}>
          <strong>{count(alerts.n, 'payment alert', 'payment alerts')}</strong>{alerts.usd > 0 && <> · <span className="font-mono tabular">{dollars(alerts.usd)}</span></>} · {windowLabel(inc)} — {alerts.stop}
        </Notice>
      )}

      {/* The period and the wallet, for Payments and Wallets alike (28 Oct 2026: "no datewise view … suppose I want to see tx in my TRC hot wallet"). */}
      {!bothDown && (view === 'payments' || view === 'wallets') && (
        <div className="mb-4 flex flex-wrap items-center gap-2 lg:mb-5">
          <PeriodChips wrap value={period} onChange={(v) => url.set({ period: v.key === 'week' ? null : v.key, from: v.key === 'custom' ? v.from ?? null : null, to: v.key === 'custom' ? v.to ?? null : null, more: null })} />
          {inc && inc.wallets.length > 1 && (
            <label className="lg:ml-auto">
              <span className="sr-only">Wallet</span>
              <select className="select w-auto pr-8 text-field lg:text-body" value={into ?? ''} onChange={(e) => url.set({ into: e.target.value || null, more: null })}>
                <option value="">All wallets</option>
                {[...new Set(inc.wallets.map((w) => w.chain))].map((c) => (
                  <optgroup key={c} label={networkWord(c)}>
                    <option value={`${c}:*`}>All {networkWord(c)}</option>
                    {inc.wallets.filter((w) => w.chain === c).map((w) => <option key={w.address} value={`${w.chain}:${w.address}`}>{w.label ?? shortAddress(w.address)}</option>)}
                  </optgroup>
                ))}
              </select>
            </label>
          )}
          {into && inc && <FilterChip label={intoWords(inc, into)} onClear={() => url.set({ into: null })} clearLabel={`Clear wallet filter ${intoWords(inc, into)}`} />}
        </div>
      )}
      {!bothDown && strip && inc && <SummaryStrip cols={4} phoneCols={2} items={stripItems(inc, pick)} />}

      {view === 'coverage' && <CoverageView d={d} inc={inc} overviewFailed={ovRead === 'failed' && !d} onRetry={() => void ov.refetch()} />}
      {!bothDown && view === 'payments' && (
        <PaymentsBody inc={inc} known={known} incRead={incRead} incFail={incFail} ovFail={ovFail}
          pick={(p) => pick({ chain: p.chain, address: p.wallet })} into={into} period={period} fresh={fresh} words={depositWords} search={search} url={url} retryInc={() => void incoming.refetch()} retryOv={() => void ov.refetch()} />
      )}
      {!bothDown && view === 'wallets' && (
        <>
          {ovFail && <Notice tone="bad" action={<button type="button" className="btn-link text-body" onClick={() => void ov.refetch()}>Try again</button>}>Screener status not read — {ovFail.why}.</Notice>}
          <WalletsV2 d={d} ovState={ovRead} inc={inc} incState={incRead} incError={incoming.error} t={treasury} budgetOut={budgetOut} pick={pick}
            onRetryOv={() => void ov.refetch()} onRetryInc={() => void incoming.refetch()} />
        </>
      )}
      {!bothDown && view === 'system' && (
        <>
          {ovFail && <Notice tone="bad" action={<button type="button" className="btn-link text-body" onClick={() => void ov.refetch()}>Try again</button>}>Screener status not read — {ovFail.why}.</Notice>}
          <SystemV2 d={d} ovState={ovRead} inc={inc} incFailed={incRead === 'failed'} nowMs={nowMs} onRetry={() => void ov.refetch()} />
        </>
      )}
    </>
  );
}

// ── The default view: the payments that need him ─────────────────────────────

function PaymentsBody({ inc, known, incRead, incFail, ovFail, pick, into, period, fresh, words, search, url, retryInc, retryOv }: {
  inc: KytIncoming | undefined;
  known: boolean;
  incRead: ReturnType<typeof readState>;
  incFail: { why: string; more: string } | null;
  ovFail: { why: string; more: string } | null;
  pick: (p: KytPayment) => boolean;
  into: string | null;
  period: PeriodValue;
  fresh: boolean;
  words: string;
  search: URLSearchParams;
  url: Url;
  retryInc: () => void;
  retryOv: () => void;
}) {
  const show: Show = SHOWS.find((s) => s.key === search.get('show'))?.key ?? 'attention';
  const tier = useTier();
  const [folded, fold] = useFoldParam();
  const [sortRaw, setSort] = useSortParam('sort', NEWEST);
  const sortAsked = legacySort(sortRaw);
  const q = search.get('q') ?? '';
  const all = search.get('more') === '1';
  const paymentId = search.get('payment');
  const traced = search.get('trace') === '1';
  const deposit = search.get('deposit') === '1';

  const missing = known && inc ? unlistedAlerts(inc) : { n: 0, usd: null, some: false };
  const nameOf = glSenderNames(inc?.payments ?? []);

  // ── The list ──
  const seat = SHOWS.find((s) => s.key === show)!;
  const needle = q.trim().toLowerCase();
  const matches = (p: KytPayment, gl: SenderName | null) => !needle || [
    p.customer, gl?.name, p.from, p.txid, p.walletLabel, String(p.amount), fmtAmount(p.amount, p.asset), p.asset,
  ].some((x) => x && x.toLowerCase().includes(needle));
  // Newest first as the base; the kit sorts within each day by the column chosen.
  const rows = inc ? sortPayments(inc.payments.filter((p) => pick(p) && seat.has(p) && matches(p, nameOf(p))), 'newest') : [];
  const visible = all ? rows : rows.slice(0, ROWS_SHOWN);
  const shown = paymentId && inc ? inc.payments.find((p) => p.id === paymentId) ?? null : null;
  const totals = dayTotals(rows);
  // Columns that tell rows apart (the register's rule): the wallet only when the list spans several, the party only when
  // a row has one — the one wallet is said once over the list, never on every row (the operator, 28 Oct 2026).
  const walletsListed = [...new Set(rows.map(walletWords))];
  const oneWallet = walletsListed.length === 1 ? walletsListed[0]! : null;
  const anyParty = rows.some((p) => p.party);
  const cols = columns(nameOf, { wallet: !oneWallet, party: anyParty }, tier);
  // A sort by a column this width does not draw (the share at 1,024, a head pressed on a wider window) is newest first here,
  // in the list and in the foot's words alike (Astra, 1 Nov 2026) — the URL keeps it for the wider window.
  const sort = sortAsked && cols.some((c) => c.key === sortAsked.key) ? sortAsked : NEWEST;
  const active = (show !== 'attention' ? 1 : 0) + (sameSort(sort, NEWEST) ? 0 : 1);
  const cardOf = card(nameOf, oneWallet);

  return (
    <>
      {incFail && (
        <Notice tone="bad" action={<button type="button" className="btn-link text-body" onClick={retryInc}>Try again</button>}>
          Payments not read — {incFail.why}.
        </Notice>
      )}
      {ovFail && <Notice tone="bad" action={<button type="button" className="btn-link text-body" onClick={retryOv}>Try again</button>}>Screener status not read — {ovFail.why}.</Notice>}
      {known && inc && !inc.group && (
        <Notice tone="warn">No KYT group — send <span className="font-mono">/kytgroup</span> in it.</Notice>
      )}

      {!(known && inc && inc.wallets.length === 0) && <SearchBar
        value={q}
        onChange={(v) => url.set({ q: v || null })}
        placeholder="Search"
        active={active}
        filters={
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2" role="group" aria-label="Show">
              {SHOWS.filter((s) => s.key === 'attention' || s.key === 'all' || s.key === show || (inc?.payments.some(s.has) ?? false)).map((s) => (
                <button key={s.key} type="button" aria-pressed={s.key === show} className={`chip ${s.key === show ? 'chip-on' : ''}`}
                  onClick={() => url.set({ show: s.key === 'attention' ? null : s.key, more: null })}>
                  {s.label}{known && inc && <span className="font-mono tabular text-tg-hint"> {whole(inc.payments.filter(s.has).length)}</span>}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Order">
              {SORTS.map((s) => (
                <button key={s.label} type="button" aria-pressed={sameSort(sort, s.sort)} className={`chip ${sameSort(sort, s.sort) ? 'chip-on' : ''}`}
                  onClick={() => setSort(s.sort)}>{s.label}</button>
              ))}
            </div>
            {/* Saved views: each one a URL (28 Oct 2026, brief item 11). */}
            <div className="flex flex-wrap gap-2" role="group" aria-label="Views">
              {PRESETS.map((v) => <Link key={v.label} to={v.to} className="chip">{v.label}</Link>)}
            </div>
          </div>
        }
      />}

      {missing.n > 0 && (
        <StateLine tone="bad" className="mb-3"
          action={inc && inc.more > 0 ? undefined : <button type="button" className="btn-link text-meta" onClick={retryInc}>Refresh</button>}>
          {count(missing.n, missing.some ? 'more alert' : 'alert', missing.some ? 'more alerts' : 'alerts')} counted{missing.usd != null && missing.usd > 0 ? ` · ${dollars(missing.usd)}` : ''} — {inc && inc.more > 0 ? 'older rows not shown' : 'list not read'}.
        </StateLine>
      )}

      {incRead === 'waiting' && !inc && <Panel flush><SkeletonRows what="the incoming payments" /></Panel>}
      {known && inc && inc.wallets.length === 0 && (
        <Notice tone="warn">No wallet watched — add one with <span className="font-mono">/watch</span>.</Notice>
      )}
      {inc && inc.wallets.length > 0 && (
        <Panel flush>
          {rows.length > 0 && oneWallet && <p className="border-b border-stroke-soft px-4 py-2 text-meta text-tg-hint lg:px-5">All into {oneWallet}</p>}
          {rows.length === 0 ? (
            <EmptyState>{into ? 'No payment into this wallet.' : emptyWords(inc, show, needle, known)}</EmptyState>
          ) : (
            // The kit's register in runs: one a Dubai day, its head carrying the day's figures and folding it (?fold=),
            // the columns sortable within each day (?sort=key:dir), the heads staying in view (28 Oct 2026).
            <Register label="Incoming payments" rowKey={(p) => p.id} onOpen={(p) => url.openSheet({ payment: p.id })} columns={cols} card={cardOf}
              groups={byDubaiDay(visible).map((g): RegisterGroup<KytPayment> => {
                const t = dayTotals(g.rows);
                return {
                  key: g.key || 'no-time',
                  head: g.label,
                  // The day's figures beside its head; on a phone the dollars go, so the day's name keeps its room (28 Oct 2026 render).
                  totals: (
                    <>
                      {whole(t.n)}<span className="max-sm:hidden"> · {dollars(t.usd)}</span>
                      {t.alerts > 0 && <span className="text-neg"> · {whole(t.alerts)} alert{t.alerts === 1 ? '' : 's'}</span>}
                      {t.watch > 0 && <span className="text-warn"> · {whole(t.watch)} watch</span>}
                    </>
                  ),
                  rows: g.rows,
                };
              })}
              folded={folded} onFold={fold} sort={sort} onSort={setSort} stickyHead />
          )}
          {rows.length > visible.length && (
            <button type="button" className="w-full border-t border-stroke-soft py-3 text-center text-body text-brand-gold hover:bg-panel-2" onClick={() => url.set({ more: '1' })}>
              {whole(rows.length - visible.length)} more →
            </button>
          )}
          {/* The totals of the filtered list, one line (28 Oct 2026). */}
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-stroke-soft px-4 py-3 text-meta text-tg-hint lg:px-5">
            <span className="font-mono tabular text-body text-tg-text">
              {count(rows.length, 'payment', 'payments')} · {dollars(totals.usd)}
              {totals.alerts > 0 && ` · ${count(totals.alerts, 'alert', 'alerts')}`}
              {totals.watch > 0 && ` · ${whole(totals.watch)} watch`}
              {known && inc.more > 0 && ` · ${whole(inc.more)} older not listed`}
            </span>
            <span className="inline-flex items-center gap-3">
              <span>{sortWords(sort)} · Dubai time</span>
              {/* What is on screen, as a file — its period and filter in the name (brief item 13). */}
              <button type="button" className="btn-link inline-flex items-center text-meta max-lg:min-h-11" onClick={() => downloadCsv(rows, `kyt-${periodWords(period).replace(/\s+/g, '-')}${show === 'attention' ? '' : `-${show}`}${into ? `-${into.replace(/[:*]/g, '_')}` : ''}.csv`)}>CSV</button>
            </span>
          </div>
        </Panel>
      )}
      {shown && !traced && (
        <Assessment p={shown} gl={nameOf(shown)} onClose={() => url.closeSheet(['payment'])} onTrace={() => url.set({ trace: '1' })} />
      )}
      {shown && traced && <PaymentTrace p={shown} gl={nameOf(shown)} onClose={() => url.set({ trace: null })} />}
      {deposit && <DepositSheet inc={known ? inc : undefined} fresh={fresh} words={words} onClose={() => url.closeSheet(['deposit'])} />}
    </>
  );
}

/** "Nothing needs you" is a claim over the whole attention list: Kyt.tsx's calm day (a wallet watched, every wallet read
 *  in full, nothing counted as not fully checked, no alert counted or listed) — and, because this list holds the watches
 *  too, no watch counted either, and every payment the worker counted listed (a counted payment with no row is a list
 *  that was not read, said as that). */
const nothingNeedsYou = (inc: KytIncoming): boolean =>
  calmDay(inc) && inc.wallets.every((w) => w.window.by.watch.n === 0 && w.month.by.watch.n === 0) && countedPayments(inc) <= inc.payments.length;

/** An empty list says what was read — never a calm day it cannot vouch for: a search names the listed rows only; a read
 *  that failed claims nothing; the calm words only where every check behind them was read (nothingNeedsYou); else the
 *  lane's own words for what was read. */
function emptyWords(inc: KytIncoming, show: Show, needle: string, known: boolean): string {
  if (needle) return 'No listed payment matches.';
  if (!known) return 'Not read.';
  if (show === 'attention' && nothingNeedsYou(inc)) return 'Nothing needs you.';
  return emptyPaymentsWords(inc);
}

/** Saved views, one URL each: this week is the default period, so a view of the week names only its filter. */
const PRESETS: ReadonlyArray<{ label: string; to: string }> = [
  { label: 'Alerts this week', to: '/kyt?show=alert' },
  { label: 'Not checked', to: '/kyt?show=unchecked' },
  { label: 'Today', to: '/kyt?period=today' },
  { label: 'Yesterday', to: '/kyt?period=yesterday' },
];

/** The filtered list as a file on his machine (nothing leaves the browser): one row a payment, the figures as figures. */
function downloadCsv(rows: readonly KytPayment[], name: string): void {
  const cell = (v: unknown): string => { const t = v == null ? '' : String(v); return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t; };
  const head = ['day', 'time', 'wallet', 'party', 'from', 'amount', 'asset', 'usd', 'finding', 'exposure_pct', 'exposure_usd', 'gl_score', 'status', 'txid'];
  const lines = rows.map((p) => {
    const r = seatOf(p);
    const risk = mainRisk(p);
    return [p.at ? dubaiDate(p.at) : '', p.at ? p.at.slice(11, 16) : '', walletWords(p), p.party?.name ?? '', p.from, p.amount, p.asset, p.usd ?? '', risk.label, risk.pct ?? '', risk.usd ?? '', p.score ?? '', r.word, p.txid].map(cell).join(',');
  });
  const blob = new Blob([[head.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ── The register's row: who · when · into which wallet, the amount, what the screening found, the lane's word ──
/** The amount: the figure never splits; the unit drops under it when the cell is short of room (a 9-digit amount ran into the
 *  Finding column — the audit, 1 Nov 2026). */
const Amount = ({ p }: { p: KytPayment }) => (
  <span className="inline-block max-w-full"><span className="whitespace-nowrap font-mono tabular">{fmtAmount(p.amount, p.asset)}</span> <span className="whitespace-nowrap text-meta text-tg-hint">{p.asset}</span></span>
);
const Chevron = () => <ChevronRight className="h-4 w-4 shrink-0 text-tg-hint" aria-hidden />;
/** Who sent it, muted: GL's name for the sending address, else the address short — never a filler word (28 Oct 2026). A name
 *  wraps to two lines, never cut mid-word ("Example Liste…" at 1024, the audit of 1 Nov 2026), the whole of it its title. */
const From = ({ p, gl }: { p: KytPayment; gl: SenderName | null }) => (p.poisoning
  // A look-alike of an address the desk knows (3 Nov 2026): red, the warning for its title — never a payee, never copied.
  ? <span className="block break-words font-mono text-meta text-neg" data-address="lookalike" title={`Address poisoning — look-alike of ${p.poisoning.of}. Never a payee; never copy.`}>{shortAddress(p.from)}</span>
  : gl
  ? <span className="line-clamp-2 break-words text-tg-hint" title={gl.label ?? gl.name}>{gl.name}</span>
  : <span className="block break-words font-mono text-meta text-tg-hint" title={p.from}>{shortAddress(p.from)}</span>);
const walletWords = (p: KytPayment): string => p.walletLabel ?? `${p.chain.toUpperCase()} desk wallet`;

/** One line a payment, in columns (the operator, 28 Oct 2026: "no tabular format"), ONE FACT A CELL (his words, 1 Nov 2026):
 *  the time under its day header, the wallet, the party the Books booked it to, who sent it (muted), the amount, what was
 *  found, the share that decided it, that share in dollars, GL's score, the lane's word. Nothing here is a sentence: the
 *  sentences are the assessment's. A name wraps, never cut; a figure never wraps. The window decides how many columns there
 *  is room for (parts.tsx useTier): all of them from a 1,400 px window; the share from 1,200; the core below — a dropped
 *  column's fact is the assessment's, one tap away. */
function columns(nameOf: (p: KytPayment) => SenderName | null, show: { wallet: boolean; party: boolean }, tier: Tier): ReadonlyArray<RegisterColumn<KytPayment>> {
  const full = tier === 'full', wide = tier !== 'core';
  const cols: Array<RegisterColumn<KytPayment> | false> = [
    { key: 'time', head: 'When', width: 'w-[56px]', cell: (p) => <Time p={p} />, sortBy: (p) => p.at ?? null, firstDir: 'desc' },
    show.wallet && { key: 'wallet', head: 'Wallet', width: 'w-[120px]', cell: (p) => <span className="block break-words" title={walletWords(p)}>{compactWallet(walletWords(p))}</span>, sortBy: (p) => walletWords(p) },
    show.party && { key: 'party', head: 'Party', width: full ? 'w-[140px]' : 'w-[18%]', cell: (p) => <span className="block break-words font-medium" title={p.party?.name}><Who p={p} /></span>, sortBy: (p) => p.party?.name ?? null },
    full && { key: 'from', head: 'From', width: 'w-[120px]', cell: (p) => <From p={p} gl={nameOf(p)} /> },
    { key: 'amount', head: 'Amount', align: 'right', width: 'w-[150px]', cell: (p) => <Amount p={p} />, sortBy: (p) => p.usd ?? p.amount },
    { key: 'finding', head: 'Finding', cell: (p) => <Finding p={p} table /> },
    wide && { key: 'share', head: 'Share', align: 'right', width: 'w-[64px]', cell: (p) => <Share p={p} />, sortBy: (p) => mainRisk(p).pct ?? null },
    full && { key: 'exposure', head: 'Exposure', align: 'right', width: 'w-[100px]', cell: (p) => <Exposed p={p} />, sortBy: (p) => mainRisk(p).usd ?? null },
    full && { key: 'score', head: 'GL score', align: 'right', width: 'w-[76px]', cell: (p) => <GlScore p={p} />, sortBy: (p) => p.score ?? null },
    { key: 'level', head: 'Status', align: 'right', width: 'w-[150px]', sortBy: (p) => riskRank(p), cell: (p) => (
      <span className="flex items-center justify-end gap-2"><LevelTag p={p} /><Chevron /></span>
    ) },
  ];
  return cols.filter((c): c is RegisterColumn<KytPayment> => !!c);
}

/** The phone's card, one fact a line: the party, or the time when nobody is booked; the time and the wallet when they are
 *  not said already; who sent it (the laptop's From column — the card used to drop it, the audit of 1 Nov 2026); the amount
 *  at the right; the finding, then the measure that decided it — its share and dollars, or GL's score — each a labelled
 *  line; the lane's word. */
function card(nameOf: (p: KytPayment) => SenderName | null, oneWallet: string | null) {
  return (p: KytPayment) => {
    const r = mainRisk(p);
    const gl = nameOf(p);
    return {
      title: (
        <>
          {p.party ? <Who p={p} /> : <Time p={p} />}
          {p.party && <span className="block font-normal text-meta text-tg-hint"><Time p={p} /></span>}
          {!oneWallet && <span className="block font-normal text-meta text-tg-hint">{compactWallet(walletWords(p))}</span>}
          <span className="block break-words font-normal text-meta text-tg-hint">
            From {p.poisoning
              ? <span className="font-mono text-neg" data-address="lookalike" title={`Address poisoning — look-alike of ${p.poisoning.of}. Never a payee; never copy.`}>{shortAddress(p.from)}</span>
              : gl ? <span title={gl.label}>{gl.name}</span> : <span className="font-mono" title={p.from}>{shortAddress(p.from)}</span>}
          </span>
        </>
      ),
      figure: <Amount p={p} />,
      meta: (
        <span className="block whitespace-normal">
          <Finding p={p} compact table />
          {r.pct != null && <CardLine label="Share"><span className="font-mono tabular">{share(r.pct)}</span></CardLine>}
          {r.usd != null && <CardLine label="Exposure"><span className="font-mono tabular">{dollars(r.usd)}</span></CardLine>}
          {r.score != null && <CardLine label="GL score"><span className="font-mono tabular">{whole(Math.round(r.score))}</span></CardLine>}
        </span>
      ),
      state: <span title={seatOf(p).word}><LevelTag p={p} /></span>,
    };
  };
}
